import { Phone, VideoCamera } from '@phosphor-icons/react';
import { useId } from '@react-aria/utils';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { Card } from '~/components/ui/card';
import { DatePicker } from '~/components/ui/date-picker';
import { FieldGroup, Fieldset, Label, Legend } from '~/components/ui/fieldset';
import { Field, FieldErrorMessage } from '~/components/ui/form';
import { Input, inputFieldClasses } from '~/components/ui/input';
import { Radio, RadioField, RadioGroupField } from '~/components/ui/radio';
import { TextArea } from '~/components/ui/textarea';
import { TimePicker } from '~/components/ui/time-picker';
import { useForm } from '~/utils/forms';

const inPersonMeetingForm = z.object({
  date: z.date(),
  time: z.date(),
  address: z.string().min(1),
  room: z.string().min(1),
  instructions: z.string().optional(),
  reportId: z.string(),
});
export function InPersonMeetingForm({
  id: formId,
  defaultValues,
  onSubmit,
}: {
  id?: string;
  defaultValues?: Partial<z.infer<typeof inPersonMeetingForm>>;
  onSubmit: (values: z.infer<typeof inPersonMeetingForm>) => void;
}) {
  const intl = useIntl();
  const form = useForm({ schema: inPersonMeetingForm, defaultValues });
  const id = useId(formId);
  const onSubmitHandler = form.handleSubmit(async (data) => {
    onSubmit(data);
  });

  return (
    <form id={id} onSubmit={onSubmitHandler} className='space-y-8'>
      <Fieldset>
        <Legend>
          {intl.formatMessage({ defaultMessage: 'Date and Time' })}
        </Legend>
        <FieldGroup>
          <Field control={form.control} field='date' shouldShowError={false}>
            <div className='grid grid-cols-4 grid-rows-[auto_minmax(auto,_1fr)] items-center gap-x-6'>
              <div className='col-span-1'>
                <Label>{intl.formatMessage({ defaultMessage: 'Date' })}</Label>
              </div>
              <div className='col-span-3'>
                <DatePicker fromDate={new Date()} />
              </div>
              <FieldErrorMessage
                control={form.control}
                field='date'
                className='col-start-2 col-span-full'
              />
            </div>
          </Field>
          <Field control={form.control} field='time' shouldShowError={false}>
            <div className='grid grid-cols-4 grid-rows-[auto_minmax(auto,_1fr)] items-center gap-x-6'>
              <div className='col-span-1'>
                <Label>{intl.formatMessage({ defaultMessage: 'Time' })}</Label>
              </div>
              <div className='col-span-3'>
                <TimePicker step={1800} />
              </div>
              <FieldErrorMessage
                control={form.control}
                field='time'
                className='col-start-2 col-span-full'
              />
            </div>
          </Field>
        </FieldGroup>
      </Fieldset>
      <Fieldset>
        <Legend>
          {intl.formatMessage({ defaultMessage: 'Meetup Location' })}
        </Legend>
        <FieldGroup>
          <Field control={form.control} field='address' shouldShowError={false}>
            <div className='grid grid-cols-4 grid-rows-[auto_minmax(auto,_1fr)] items-center gap-x-6'>
              <div className='col-span-1'>
                <Label>
                  {intl.formatMessage({ defaultMessage: 'Address' })}
                </Label>
              </div>
              <div className='col-span-3'>
                <Input />
              </div>
              <FieldErrorMessage
                control={form.control}
                field='address'
                className='col-start-2 col-span-full'
              />
            </div>
          </Field>
          <Field control={form.control} field='room' shouldShowError={false}>
            <div className='grid grid-cols-4 grid-rows-[auto_minmax(auto,_1fr)] items-center gap-x-6'>
              <Label>{intl.formatMessage({ defaultMessage: 'Room' })}</Label>
              <div className='col-span-3'>
                <Input />
              </div>
              <FieldErrorMessage
                control={form.control}
                field='room'
                className='col-start-2 col-span-full'
              />
            </div>
          </Field>
          <Field control={form.control} field='instructions'>
            <div className='grid grid-cols-4 items-center gap-6'>
              <Label>
                {intl.formatMessage({ defaultMessage: 'Instructions' })}{' '}
                {intl.formatMessage({ defaultMessage: '(Optional)' })}
              </Label>
              <div className='col-span-3'>
                <TextArea minRows={5} />
              </div>
            </div>
          </Field>
        </FieldGroup>
      </Fieldset>
    </form>
  );
}

const CALL_TYPES = {
  VIDEO: 2,
  VOICE: 1,
} as const;
const callTypesSchema = z.nativeEnum(CALL_TYPES);
const preScheduledMeetingSchema = z.object({
  date: z.date(),
  callType: z.preprocess((v) => z.coerce.number().parse(v), callTypesSchema),
  time: z.date(),
  reportId: z.string(),
});
export function PreScheduledMeetingForm({
  id,
  defaultValues,
  onSubmit,
}: {
  id?: string;
  defaultValues?: Partial<z.infer<typeof preScheduledMeetingSchema>>;
  onSubmit: (
    values: z.infer<typeof preScheduledMeetingSchema>,
  ) => PromiseLike<void>;
}) {
  const intl = useIntl();
  const formId = useId(id);
  const form = useForm({ schema: preScheduledMeetingSchema, defaultValues });
  const onSubmitHandler = form.handleSubmit(onSubmit);

  return (
    <form id={formId} onSubmit={onSubmitHandler}>
      <FieldGroup>
        <RadioGroupField
          aria-label='Select a type'
          control={form.control}
          field='callType'
        >
          <div className='grid grid-cols-2 gap-4'>
            <Card
              size='sm'
              sx={{ justifyItems: 'center' }}
              orientation='horizontal'
            >
              <span className='relative z-10'>
                <VideoCamera />
              </span>
              <Radio
                value={CALL_TYPES.VIDEO}
                label='Video Call'
                overlay
                disableIcon
              />
            </Card>
            <Card
              size='sm'
              sx={{ justifyItems: 'center' }}
              orientation='horizontal'
            >
              <span className='relative z-10'>
                <Phone />
              </span>
              <RadioField
                value={CALL_TYPES.VOICE}
                label='Phone Call'
                overlay
                disableIcon
              />
            </Card>
          </div>
        </RadioGroupField>
        <Field control={form.control} field='date'>
          <div className={clsx(inputFieldClasses)}>
            <Label>{intl.formatMessage({ defaultMessage: 'Date' })}</Label>
            <DatePicker fromDate={new Date()} />
          </div>
        </Field>
        <Field control={form.control} field='time'>
          <div className={clsx(inputFieldClasses)}>
            <Label>{intl.formatMessage({ defaultMessage: 'Time' })}</Label>
            <TimePicker />
          </div>
        </Field>
      </FieldGroup>
    </form>
  );
}
